import { useCallback, useEffect, useRef } from 'react'
import type { Dispatch, SetStateAction } from 'react'
import { loadLeagues as loadLeaguesApi } from '../api/leagues'
import type { League } from '../types'

/** How recently a *navigation* refresh must have succeeded for the next one to be skipped. A pull
 * ignores this: it is an explicit request for a round trip. */
export const LEAGUES_REFRESH_THROTTLE_MS = 10_000

/** What asked for the refresh. The two differ in three ways only — the throttle, the visible
 * indicator (the caller's business) and whether a failure says anything. */
export type LeaguesRefreshTrigger = 'navigation' | 'pull'

export interface LeaguesRefreshController {
  /** Resolves when the refresh has settled — the pull indicator stays up until it does. A call
   *  made while one is already in flight returns that same promise instead of firing again. */
  refresh: (trigger: LeaguesRefreshTrigger) => Promise<void>
}

export interface UseLeaguesRefreshOptions {
  setLeagues: Dispatch<SetStateAction<League[]>>
  /** Reads `useLeagues`' mutation counter. Captured before the request and re-read after it, so a
   *  local mutation that lands mid-flight discards the older server list. */
  readMutationToken: () => number
  onToast: (message: string) => void
  /** Injected by the tests; production always uses the real endpoint. */
  loadLeagues?: () => Promise<League[]>
  now?: () => number
}

/**
 * The two ways `/leagues` gets refreshed without a mutation behind it: arriving on
 * the Leagues tab, and pulling down on either league screen. Both land here so they share one
 * in-flight request, one throttle and one staleness rule.
 *
 * Failure is deliberately quiet on the navigation path — nobody asked, and the list on screen is
 * stale rather than wrong, so it is never blanked or covered. A pull did ask, so it gets one toast.
 */
export function useLeaguesRefresh({
  setLeagues,
  readMutationToken,
  onToast,
  loadLeagues = loadLeaguesApi,
  now = Date.now,
}: UseLeaguesRefreshOptions): LeaguesRefreshController {
  const inFlightRef = useRef<Promise<void> | null>(null)
  const lastSuccessRef = useRef(0)

  // Read through refs so `refresh` keeps a stable identity — it is passed to gesture hooks that
  // re-bind their touch listeners whenever it changes.
  const optionsRef = useRef({ setLeagues, readMutationToken, onToast, loadLeagues, now })
  useEffect(() => {
    optionsRef.current = { setLeagues, readMutationToken, onToast, loadLeagues, now }
  })

  const refresh = useCallback((trigger: LeaguesRefreshTrigger) => {
    const o = optionsRef.current
    if (inFlightRef.current) return inFlightRef.current
    if (
      trigger === 'navigation' &&
      o.now() - lastSuccessRef.current < LEAGUES_REFRESH_THROTTLE_MS
    ) {
      return Promise.resolve()
    }

    const tokenAtStart = o.readMutationToken()
    const run = async () => {
      try {
        const leagues = await o.loadLeagues()
        // A create/join/leave/invite/motto landed while this was in the air. Its locally patched
        // list is newer than the one we just fetched, and applying ours would put a league the
        // user just left straight back on screen. Drop the response instead.
        if (optionsRef.current.readMutationToken() !== tokenAtStart) return
        lastSuccessRef.current = optionsRef.current.now()
        optionsRef.current.setLeagues(leagues)
      } catch {
        if (trigger === 'pull') optionsRef.current.onToast("Couldn't refresh. Try again.")
      }
    }

    // `.finally` never runs synchronously, so the ref below is always assigned before it clears.
    const promise = run().finally(() => {
      inFlightRef.current = null
    })
    inFlightRef.current = promise
    return promise
  }, [])

  return { refresh }
}
